import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, Package, Barcode as BarcodeIcon, Tag, Layers,
    ShoppingBasket, Ruler, Boxes, Loader2, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

import {
    Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { portalCatalogAPI } from '../../lib/portalApi';
import ProductImage from '../ProductImage';
import AddToCart from './AddToCart';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';

// ----- Stock label --------------------------------------------------------
const StockLabel = ({ status }) => {
    const { t } = useTranslation('portal');
    const cfg = {
        in_stock: { label: t('catalog.stock.in_stock'), cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
        low:      { label: t('catalog.stock.low'),      cls: 'bg-amber-50 text-amber-700 border-amber-200' },
        out:      { label: t('catalog.stock.out'),      cls: 'bg-red-50 text-red-700 border-red-200' },
    }[status] || { label: status, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.cls}`}
            data-testid={`quickview-stock-${status}`}>
            {cfg.label}
        </span>
    );
};

// ----- Info row -----------------------------------------------------------
const InfoRow = ({ icon: Icon, label, value, testid }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex items-center justify-between gap-4 py-2.5 border-b border-[#EFE5E1] last:border-b-0" data-testid={testid}>
            <div className="flex items-center gap-2 text-[13px] text-gray-500">
                <Icon size={14} className="text-[#C08478]" />
                {label}
            </div>
            <div className="text-[13px] text-[#1A1A1A] font-medium text-right">
                {value}
            </div>
        </div>
    );
};

// ----- Related product tile ----------------------------------------------
const RelatedTile = ({ p, onOpen }) => (
    <button
        onClick={() => onOpen(p.sku)}
        data-testid={`quickview-related-${p.sku}`}
        className="text-left group bg-white border border-[#EFE5E1] rounded-lg overflow-hidden hover:shadow-md hover:border-[#C08478]/40 transition-all"
    >
        <div className="aspect-square bg-gradient-to-br from-[#FBF8F6] to-[#F5EEEA] overflow-hidden">
            <ProductImage
                product={p}
                imgClassName="group-hover:scale-[1.05] transition-transform duration-500"
                testid={`quickview-related-${p.sku}-img`}
            />
        </div>
        <div className="p-2.5 space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-gray-400 truncate">{p.brand}</div>
            <div className="text-[12px] text-[#1A1A1A] font-medium leading-snug line-clamp-2 min-h-[32px]">{p.name}</div>
            {!p.price_hidden && (
                <div className="text-[13px] font-bold text-[#1A1A1A] tabular-nums">€{Number(p.price).toFixed(2)}</div>
            )}
        </div>
    </button>
);

// ----- Related carousel --------------------------------------------------
const RelatedRow = ({ title, items, onOpen }) => {
    if (!items || items.length === 0) return null;
    return (
        <div className="space-y-3">
            <h4 className="text-[11px] uppercase tracking-[2px] text-gray-500 font-semibold flex items-center gap-1.5">
                <ChevronRight size={12} className="text-[#C08478]" /> {title}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {items.map((p) => (
                    <RelatedTile key={p.sku} p={p} onOpen={onOpen} />
                ))}
            </div>
        </div>
    );
};

// ----- Modal --------------------------------------------------------------
/**
 * Product Quick-view modal for the B2B portal.
 *
 * Opens on product-card click; loads the full product detail from
 * `/portal/catalog/products/{sku}` and renders a two-column layout:
 *
 *   Left  → large product image (with zoom on hover) + gallery thumbnails
 *   Right → identity, commercial info, description, add-to-cart, related
 *
 * The modal is keyed by `sku` so opening a related tile swaps the content
 * in-place without unmounting the dialog. Add-to-cart calls flow through
 * the existing `<AddToCart>` component so cart-context / MOQ logic stays
 * the single source of truth.
 */
export const PortalProductQuickView = ({ sku, open, onOpenChange }) => {
    const { t } = useTranslation('portal');
    const [activeSku, setActiveSku] = useState(sku);
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
    const { push: pushRecent } = useRecentlyViewed();

    // Keep the internal SKU in sync whenever the parent re-opens with a
    // different SKU (e.g. clicking a related-product tile).
    useEffect(() => {
        setActiveSku(sku);
    }, [sku]);

    // Fetch on open + on sku change. Also scroll dialog scroll-body back
    // to the top so long descriptions from the previous product don't
    // leave the reader mid-modal after clicking a related tile.
    useEffect(() => {
        if (!open || !activeSku) return;
        let cancelled = false;
        setLoading(true);
        portalCatalogAPI.detail(activeSku)
            .then(({ data }) => {
                if (cancelled) return;
                if (data?.error) {
                    toast.error(t('catalog.detail.not_found', { defaultValue: 'Product not found' }));
                    onOpenChange(false);
                    return;
                }
                setProduct(data);
                // Persist to Recently-viewed so the reel picks it up
                // as soon as the customer returns to the catalog home.
                pushRecent({
                    id: data.id,
                    sku: data.sku,
                    name: data.name,
                    brand: data.brand,
                    brand_logo_url: data.brand_logo_url,
                    image_url: data.image_url,
                    price: data.price,
                    price_hidden: data.price_hidden,
                    stock_status: data.stock_status,
                });
                // Radix DialogContent sets overflow-y-auto on itself.
                requestAnimationFrame(() => {
                    document
                        .querySelector('[data-testid="portal-quickview-dialog"]')
                        ?.scrollTo?.({ top: 0, behavior: 'smooth' });
                });
            })
            .catch(() => {
                if (cancelled) return;
                toast.error(t('common.load_failed', { defaultValue: 'Failed to load product' }));
                onOpenChange(false);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [open, activeSku, onOpenChange, pushRecent, t]);

    // Free the product state on close so the next open shows the loader
    // instead of a stale image flash.
    useEffect(() => {
        if (!open) {
            const timer = setTimeout(() => setProduct(null), 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="portal-quickview-dialog"
                className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 bg-white border-[#EFE5E1]"
            >
                <DialogTitle className="sr-only">
                    {product?.name || t('catalog.detail.title', { defaultValue: 'Product details' })}
                </DialogTitle>
                <DialogDescription className="sr-only">
                    {t('catalog.detail.aria_description', {
                        defaultValue: 'Full product details including images, price, stock, description and related products.',
                    })}
                </DialogDescription>

                {loading && !product && (
                    <div className="flex items-center justify-center min-h-[400px]" data-testid="portal-quickview-loading">
                        <Loader2 size={28} className="animate-spin text-[#C08478]" />
                    </div>
                )}

                {product && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                        {/* -------- Left: image + gallery ------------------- */}
                        <div className="bg-gradient-to-br from-[#FBF8F6] to-[#F5EEEA] p-6 md:p-8 flex flex-col">
                            <div
                                className="relative aspect-square rounded-xl overflow-hidden bg-white cursor-zoom-in"
                                data-testid="portal-quickview-image-wrap"
                                onMouseEnter={() => setZoom((z) => ({ ...z, active: true }))}
                                onMouseLeave={() => setZoom((z) => ({ ...z, active: false }))}
                                onMouseMove={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    setZoom({
                                        active: true,
                                        x: ((e.clientX - r.left) / r.width) * 100,
                                        y: ((e.clientY - r.top) / r.height) * 100,
                                    });
                                }}
                            >
                                <ProductImage
                                    product={product}
                                    imgClassName="w-full h-full object-cover transition-transform duration-300"
                                    testid="portal-quickview-image"
                                    loading="eager"
                                />
                                {zoom.active && product.image_url && (
                                    <div
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            backgroundImage: `url(${product.image_url})`,
                                            backgroundSize: '200%',
                                            backgroundPosition: `${zoom.x}% ${zoom.y}%`,
                                        }}
                                    />
                                )}
                            </div>
                            {product.gallery_urls && product.gallery_urls.length > 0 && (
                                <div className="grid grid-cols-4 gap-2 mt-3" data-testid="portal-quickview-gallery">
                                    {[product.image_url, ...product.gallery_urls].filter(Boolean).slice(0, 4).map((u, i) => (
                                        <div key={i} className="aspect-square rounded-md overflow-hidden bg-white border border-[#EFE5E1]">
                                            <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* -------- Right: identity + commercial + actions ------ */}
                        <div className="p-6 md:p-8 space-y-5">
                            {/* Header */}
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    {product.brand && (
                                        <div className="flex items-center gap-2 mb-2">
                                            {product.brand_logo_url && (
                                                <img
                                                    src={product.brand_logo_url}
                                                    alt={product.brand}
                                                    className="h-6 w-auto object-contain"
                                                    loading="lazy"
                                                />
                                            )}
                                            <span className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold" data-testid="portal-quickview-brand">
                                                {product.brand}
                                            </span>
                                        </div>
                                    )}
                                    <h2 className="text-xl md:text-2xl font-bold text-[#1A1A1A] leading-tight" data-testid="portal-quickview-name">
                                        {product.marketing_title || product.name}
                                    </h2>
                                    {product.marketing_title && product.marketing_title !== product.name && (
                                        <p className="text-[13px] text-gray-500 mt-1">{product.name}</p>
                                    )}
                                </div>
                                <button
                                    aria-label={t('common.close', { defaultValue: 'Close' })}
                                    onClick={() => onOpenChange(false)}
                                    data-testid="portal-quickview-close"
                                    className="text-gray-400 hover:text-[#1A1A1A] transition-colors p-1 rounded-md hover:bg-[#FBF8F6]"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Stock + price */}
                            <div className="flex items-center justify-between gap-3">
                                <StockLabel status={product.stock_status} />
                                {product.price_hidden ? (
                                    <span className="text-[13px] italic text-[#C08478]" data-testid="portal-quickview-price-hidden">
                                        {t('catalog.price_on_request', { defaultValue: 'Price on request' })}
                                    </span>
                                ) : (
                                    <div className="text-right" data-testid="portal-quickview-price">
                                        <div className="text-2xl font-bold text-[#1A1A1A] tabular-nums leading-none">
                                            €{Number(product.price).toFixed(2)}
                                        </div>
                                        <div className="text-[10px] text-gray-400 mt-1">{t('catalog.price_per_unit', { defaultValue: 'per' })} {product.unit}</div>
                                    </div>
                                )}
                            </div>

                            {/* Info grid */}
                            <div className="rounded-lg border border-[#EFE5E1] bg-[#FBF8F6]/50 px-4 py-1">
                                <InfoRow icon={Tag}         label="SKU" value={<span className="font-mono">{product.sku}</span>} testid="portal-quickview-info-sku" />
                                <InfoRow icon={BarcodeIcon} label={t('catalog.detail.barcode', { defaultValue: 'Barcode' })} value={product.barcode ? <span className="font-mono">{product.barcode}</span> : null} testid="portal-quickview-info-barcode" />
                                <InfoRow icon={Layers}      label={t('catalog.detail.category', { defaultValue: 'Category' })} value={product.category} testid="portal-quickview-info-category" />
                                <InfoRow icon={Boxes}       label={t('catalog.detail.units_per_carton', { defaultValue: 'Units / carton' })} value={product.units_per_carton} testid="portal-quickview-info-carton" />
                                <InfoRow icon={Ruler}       label={t('catalog.detail.package', { defaultValue: 'Package' })} value={product.package_description} testid="portal-quickview-info-package" />
                                <InfoRow icon={ShoppingBasket} label={t('catalog.detail.moq', { defaultValue: 'Min. order' })} value={`${product.moq} ${product.unit}`} testid="portal-quickview-info-moq" />
                            </div>

                            {/* Description */}
                            {(product.description || product.marketing_description) && (
                                <div data-testid="portal-quickview-description">
                                    <h3 className="text-[11px] uppercase tracking-[2px] text-gray-500 font-semibold mb-2">
                                        {t('catalog.detail.description', { defaultValue: 'Description' })}
                                    </h3>
                                    <div className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-line">
                                        {product.marketing_description || product.description}
                                    </div>
                                </div>
                            )}

                            {/* Add to cart */}
                            <div className="pt-2" data-testid="portal-quickview-actions">
                                <AddToCart product={{ ...product, brand_logo_url: product.brand_logo_url }} variant="row" />
                            </div>
                        </div>
                    </div>
                )}

                {/* Related products */}
                {product && (product.related?.by_brand?.length > 0 || product.related?.by_category?.length > 0) && (
                    <div className="border-t border-[#EFE5E1] p-6 md:p-8 space-y-6 bg-[#FBF8F6]/40">
                        <RelatedRow
                            title={t('catalog.detail.related_brand', { defaultValue: 'More from this brand' })}
                            items={product.related.by_brand}
                            onOpen={setActiveSku}
                        />
                        <RelatedRow
                            title={t('catalog.detail.related_category', { defaultValue: 'Similar products' })}
                            items={product.related.by_category}
                            onOpen={setActiveSku}
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PortalProductQuickView;
